import { useEffect, useRef, useState } from "react";
import { getProductById } from "../../../apis/product";

export type ProductStatus = "checking" | "valid" | "invalid";
export interface ProductInfo {
  status: ProductStatus;
  name?: string;
  brand?: string;
}

/** Validates each "<barcode>/<matCode>" key against ProductLibProduct, once, cached. */
export function useProductValidation(productKeys: string[]): Record<string, ProductInfo> {
  const [info, setInfo] = useState<Record<string, ProductInfo>>({});
  const requested = useRef<Set<string>>(new Set());

  // Cancellation belongs to the component's lifetime, not to one run of the effect.
  // `productKeys` is a fresh array on every drafts change, so tying it to the effect meant
  // a second dropped folder cancelled the first one's in-flight lookup — and because the
  // key was already in `requested`, nothing ever retried it and the product sat on
  // "checking" forever, which also kept its photos out of the uploadable set.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    for (const key of productKeys) {
      if (requested.current.has(key)) continue;
      requested.current.add(key);
      const [barcode, matCode] = key.split("/");
      setInfo((prev) => ({ ...prev, [key]: { status: "checking" } }));
      // eslint-disable-next-line no-loop-func
      (async () => {
        try {
          const product = await getProductById(barcode, matCode);
          if (!alive.current) return;
          setInfo((prev) => ({
            ...prev,
            [key]: product
              ? { status: "valid", name: product.name ?? "", brand: product.brand ?? undefined }
              : { status: "invalid" },
          }));
        } catch {
          if (alive.current) setInfo((prev) => ({ ...prev, [key]: { status: "invalid" } }));
        }
      })();
    }
  }, [productKeys]);

  return info;
}
